import { Replay } from '@mui/icons-material';
import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel } from '@mui/material';
import {
  GridColDef,
  GridColumnGroupingModel,
  GridColumnVisibilityModel,
  type GridValueFormatter,
} from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { StyleObj } from '../../@types';
import { useModal } from '../../contexts/ModalContext';
import usePersist from '../../hooks/usePersist';

export type AdditionalDataGridColumnProps = {
  alwaysVisible?: boolean;
  skipFormatting?: (...args: Parameters<GridValueFormatter>) => boolean;
  hideZeros?: boolean;
};

type CustomGridColDef = GridColDef & AdditionalDataGridColumnProps;

type CustomizeTableProps = {
  columns: readonly CustomGridColDef[];
  visibleColumns: GridColumnVisibilityModel;
  setVisibleColumns: React.Dispatch<React.SetStateAction<GridColumnVisibilityModel>>;
  defaultVisibleColumns: GridColumnVisibilityModel;
  visibleColumnsLSKey: string;
  columnGroupingModel?: GridColumnGroupingModel;
};

const styles: StyleObj = {
  formControlLabel: (theme) => ({
    '.MuiFormControlLabel-label': { ...theme.typography.h6 },
  }),
  checkList: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
  },
};

const CustomizeTable = ({
  columns,
  visibleColumns,
  setVisibleColumns,
  defaultVisibleColumns,
  visibleColumnsLSKey,
  columnGroupingModel,
}: CustomizeTableProps) => {
  const { setPersistData, deletePersistData } = usePersist(false);
  const [visibleColumnsLocal, setVisibleColumnsLocal] = useState<GridColumnVisibilityModel>(visibleColumns);
  const { closeModal } = useModal();

  const fieldToGroupMap = useMemo(() => {
    if (!columnGroupingModel) return {};

    const map: Record<string, string> = {};

    columnGroupingModel.forEach((group) => {
      group.children?.forEach((child) => {
        if ('field' in child) {
          map[child.field] = group.groupId;
        }
      });
    });

    return map;
  }, [columnGroupingModel]);

  const getColumnLabel = (column: CustomGridColDef) => {
    const groupName = fieldToGroupMap[column.field];
    if (groupName) {
      return `${groupName} - ${column.headerName}`;
    }
    return column.headerName;
  };

  const handleSave = () => {
    setPersistData(visibleColumnsLSKey, visibleColumnsLocal);
    setVisibleColumns(visibleColumnsLocal);
    closeModal?.();
  };

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    setVisibleColumnsLocal((prev) => ({
      ...prev,
      [field]: e.target.checked,
    }));
  };

  const handleReset = () => {
    setVisibleColumns(defaultVisibleColumns);
    setVisibleColumnsLocal(defaultVisibleColumns);
    deletePersistData(visibleColumnsLSKey);
    closeModal?.();
  };

  return (
    <>
      <DialogTitle variant='h4'>Customize table</DialogTitle>
      <DialogContent sx={styles.checkList}>
        {columns.map((column) => {
          if (column.field === 'edit') return null;

          const isChecked = column.alwaysVisible ?? visibleColumnsLocal[column.field];

          return (
            <FormControlLabel
              key={column.field}
              control={<Checkbox checked={isChecked} onChange={(e) => handleCheck(e, column.field)} disableRipple />}
              label={getColumnLabel(column)}
              sx={styles.formControlLabel}
              disabled={column.alwaysVisible}
            />
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button variant='text' onClick={handleReset} startIcon={<Replay />} size='small'>
          Reset filters
        </Button>
        <Button variant='outlined' onClick={closeModal}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </>
  );
};

export default CustomizeTable;
